/**
 * The refuter — the one call whose only job is to destroy a finding.
 *
 * ⛔ THE INVARIANT, VERBATIM: **"Dropping requires evidence; doubt does not."**
 *
 * That sentence is the whole design. A refuter that may drop a finding because it is unconvinced is
 * a second opinion, and a second opinion from the same model family agrees with the first often
 * enough to be worthless. This one may drop a finding only when it produces a citation that resolves
 * in the clone and quotes that line byte for byte — the same standard the finding itself had to
 * meet, applied in reverse. Everything weaker than that is doubt, and doubt keeps the finding.
 *
 * ⭐ WHY A SEPARATE CALL AT ALL. docs/ARCHITECTURE.md: *Refute-or-Promote* (arXiv:2604.19049) — a
 * promoter proposes, a refuter actively tries to disprove, only survivors emit. Asking the finder to
 * check its own work asks it to abandon a position it has just argued for, which is the thing models
 * are worst at.
 *
 * ⛔ FRESH CONTEXT, ONCE, AT THE END. Not a debate and not a loop. One refutation call per surviving
 * candidate, carrying the CLAIM and the code — and deliberately NOT the finder's reasoning. Handing
 * over the argument that produced the finding re-creates exactly the anchoring the separate call
 * exists to break: the refuter would be grading an essay instead of reading code.
 * ⚠️ That is a decision with a real alternative — showing the reasoning lets the refuter attack the
 * inference directly rather than re-deriving it, and it is cheaper. It is recorded here so the next
 * person changes it deliberately rather than by accident.
 *
 * ⭐ ORDER THIS AFTER THE FREE GATES. `computeComponents` costs nothing and drops a fabricated quote
 * outright; refuting a finding whose citation does not even resolve is paying a model to read
 * fiction. The gates run first, the refuter runs on what is left.
 */

import { CloneError, readFileAt } from "../repo/clone.js"
import type { Backend, BackendRequest, BackendResult, JsonSchema, SystemPrompt } from "../backend/types.js"
import type { EffortLevel } from "../config/schema.js"
import type { CandidateFinding, ReviewClone } from "./candidate.js"
import { renderFailureScenario } from "./candidate.js"

/**
 * ⛔ Exported as a constant, not written in a comment, so the rule this module exists to enforce is
 * greppable, testable, and cannot be softened without a diff that says so.
 */
export const REFUTER_INVARIANT = "Dropping requires evidence; doubt does not."

export type RefutationVerdict =
  /** Disproved, WITH a citation that resolves and quotes its line byte for byte. The finding dies. */
  | "refuted"
  /** The refuter engaged and could not disprove it. The finding survives, and is credited for it. */
  | "stands"
  /**
   * The refuter did not reach a usable answer — it errored, timed out, refused, was truncated, or
   * returned something we could not read.
   * ⛔ NOT a synonym for "stands" and NOT a synonym for "refuted". The finding survives, because
   * dropping requires evidence, but it is not credited with having survived anything.
   */
  | "not_run"

/** Where the refuter says the finding is wrong. Checked against the clone before it is believed. */
export interface RefutationEvidence {
  readonly path: string
  readonly line: number
  /** Byte-identical to that line, or this is not evidence. */
  readonly quote: string
}

export interface RefutationResult {
  readonly verdict: RefutationVerdict
  /** One line, in plain language: why this verdict. Shown in triage next to the finding. */
  readonly why: string
  /** The refuter's own reasoning, captured for the audit trail. */
  readonly reasoning: string
  /** Present only on `refuted`, and only after it has been verified against the clone. */
  readonly disproof?: RefutationEvidence
  /** What the backend actually returned, kept so a verdict can be re-derived by hand. */
  readonly raw?: string
}

/** The honest default for a finding nothing has attacked yet. */
export const NOT_REFUTED: RefutationResult = {
  verdict: "not_run",
  why: "the refuter has not run against this finding",
  reasoning: "",
}

/**
 * ⭐ Same shape discipline as a dimension: reasoning first, verdict last. The refuter is a model
 * stating a verdict, and it gets the same ordering for the same measured reason.
 */
export const REFUTER_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["reasoning", "why", "verdict"],
  properties: {
    reasoning: {
      type: "string",
      description:
        "What you checked in the code, and what you found. Written BEFORE you decide. If you are about to say 'refuted', this is where the guard, the caller or the constraint that makes the scenario impossible is named.",
    },
    why: { type: "string", description: "One line, plain language, stating what settled it." },
    disproof: {
      type: "object",
      additionalProperties: false,
      required: ["path", "line", "quote"],
      description:
        "REQUIRED to refute. The line that disproves the finding, quoted byte for byte. A refutation with no citation that resolves is treated as 'stands'.",
      properties: {
        path: { type: "string" },
        line: { type: "integer", minimum: 1 },
        quote: { type: "string" },
      },
    },
    // ⛔ LAST.
    verdict: { type: "string", enum: ["refuted", "stands"] },
  },
}

/**
 * The instruction block — [D] in the cache layout, last and uncached.
 *
 * ⛔ Carries the claim and not the argument. See the file header.
 */
export function refutationInstruction(finding: CandidateFinding): string {
  const anchor = finding.anchor
  const range = anchor.startLine === undefined ? `${anchor.line}` : `${anchor.startLine}-${anchor.line}`
  return [
    "# Refutation",
    "",
    "Another reviewer has made the claim below about this change. **Your only job is to disprove it.**",
    "You are not here to agree with it, improve it, re-severity it or add to it.",
    "",
    "⛔ " + REFUTER_INVARIANT,
    "",
    "That is not a slogan, it is the rule you are graded on. You may answer `refuted` **only** when",
    "you can point at a line of code that makes the claim false — a guard that already runs, a caller",
    "that already checks, a constraint that cannot hold the value, a cap that already bounds it, a",
    "type that excludes the case. Quote that line byte for byte in `disproof`, with its real path and",
    "line number. It is read back out of the tree and compared character by character; a citation that",
    "does not resolve, or a quote that does not match, is discarded and the finding lives.",
    "",
    "If you merely doubt it — the scenario seems unlikely, the severity feels high, you would have",
    "written it differently, you cannot find the guard but suspect one exists — the answer is",
    "`stands`. Say so plainly. A finding that survives you is not a finding you endorsed; it is one",
    "nobody could disprove, and a person decides what to do with it.",
    "",
    "## The claim",
    "",
    `- **Dimension:** ${finding.dimensionId}`,
    `- **Title:** ${finding.title}`,
    `- **Cited at:** ${anchor.path}:${range} (${anchor.side})`,
    "- **Quoted code:**",
    "",
    "```",
    finding.quote,
    "```",
    "",
    `- **Claimed consequence:** ${finding.consequence}`,
    `- **Claimed failure:** ${renderFailureScenario(finding.failureScenario)}`,
    ...(finding.ruleCitation === undefined
      ? []
      : [`- **Claimed rule broken:** ${finding.ruleCitation.text} — ${finding.ruleCitation.source}`]),
    "",
    "## How to answer",
    "",
    "Emit `reasoning` first, then `why`, then `disproof` if and only if you have one, and `verdict`",
    "LAST. Read the code before you decide; the claim above is not evidence of anything.",
  ].join("\n")
}

export interface RefuteContext {
  readonly backend: Backend
  /** Read to verify the refuter's own citation. The same tree the finding was checked against. */
  readonly clone: ReviewClone
  /** [A] and [B] — the cached prefix, identical to the one the dimensions ran against. */
  readonly systemPrompt: SystemPrompt
  /** [C] The diff under review. */
  readonly diff: string
  readonly model?: string
  readonly effort?: EffortLevel
}

/**
 * Run the refutation for one finding.
 *
 * ⛔ Never throws for a backend failure. An outage is not evidence that a finding is wrong, so it
 * becomes `not_run` and the finding continues — see the invariant.
 */
export async function refute(finding: CandidateFinding, ctx: RefuteContext): Promise<RefutationResult> {
  const request: BackendRequest = {
    dimensionId: `refute:${finding.dimensionId}`,
    systemPrompt: ctx.systemPrompt,
    diff: ctx.diff,
    dimensionInstruction: refutationInstruction(finding),
    outputSchema: REFUTER_SCHEMA,
    ...(ctx.clone.path === "" ? {} : { workdir: ctx.clone.path }),
    ...(ctx.model === undefined ? {} : { model: ctx.model }),
    ...(ctx.effort === undefined ? {} : { effort: ctx.effort }),
  }

  let result: BackendResult
  try {
    result = await ctx.backend.run(request)
  } catch (err) {
    return {
      verdict: "not_run",
      why: `the refuter could not run: ${err instanceof Error ? err.message : String(err)}`,
      reasoning: "",
    }
  }

  // ⚠️ `stop` is not always success. A refusal returns 200 with output that may not match the
  // schema, and a truncation returns a partial object — both look fine to anything checking only
  // that a call completed. A half-written refutation is not evidence.
  if (result.stopReason === "refusal" || result.stopReason === "max_tokens") {
    return {
      verdict: "not_run",
      why: `the refutation stopped early (${result.stopReason}), so it disproved nothing`,
      reasoning: "",
      raw: result.raw,
    }
  }

  return judgeRefutation(result.output, finding, ctx.clone, result.raw)
}

/**
 * Turn whatever the refuter said into a verdict, and verify its evidence against the tree.
 *
 * ⭐ Pure, and exported, because this is where the invariant actually lives. It is tested directly,
 * with no backend involved, and every path that could turn doubt into a drop is one of these
 * branches.
 */
export function judgeRefutation(
  output: unknown,
  finding: CandidateFinding,
  clone: ReviewClone,
  raw?: string,
): RefutationResult {
  const withRaw = raw === undefined ? {} : { raw }
  const parsed = asRecord(output)
  if (parsed === undefined) {
    return {
      verdict: "not_run",
      why: "the refuter returned nothing we could read, so nothing was disproved",
      reasoning: "",
      ...withRaw,
    }
  }

  const reasoning = asString(parsed["reasoning"]) ?? ""
  const why = asString(parsed["why"]) ?? ""
  const verdict = asString(parsed["verdict"])

  if (verdict !== "refuted" && verdict !== "stands") {
    return {
      verdict: "not_run",
      why: `the refuter answered ${JSON.stringify(verdict ?? null)}, which is neither "refuted" nor "stands"`,
      reasoning,
      ...withRaw,
    }
  }

  if (verdict === "stands") {
    return {
      verdict: "stands",
      why: why === "" ? "the refuter could not disprove it" : why,
      reasoning,
      ...withRaw,
    }
  }

  // ── It says refuted. Now make it prove that. ──
  //
  // ⛔ A claim of refutation with no usable citation is NOT a drop, and it is NOT `not_run` either:
  // the refuter ran, read the code, and failed to produce the one thing that would have killed the
  // finding. That is a survival, and the finding is credited with it.
  const disproof = readEvidence(parsed["disproof"])
  if (disproof === undefined) {
    return {
      verdict: "stands",
      why: "the refuter said it was refuted but cited no line; " + REFUTER_INVARIANT.toLowerCase(),
      reasoning,
      ...withRaw,
    }
  }

  let actual: string | undefined
  try {
    actual = readFileAt(clone.path, disproof.path, disproof.line)
  } catch (err) {
    // ⚠️ A path escape or a malformed line number. clone.ts throws rather than returning undefined
    // so that an attack is never reported as an ordinary miss — here it means the citation is
    // unusable, which is the same outcome as any other unusable citation: the finding lives.
    return {
      verdict: "stands",
      why: `the refuter cited ${disproof.path}:${disproof.line}, which cannot be read (${
        err instanceof CloneError ? err.code : "unreadable"
      })`,
      reasoning,
      ...withRaw,
    }
  }

  if (actual === undefined) {
    return {
      verdict: "stands",
      why: `the refuter cited ${disproof.path}:${disproof.line}, which does not exist in the reviewed tree`,
      reasoning,
      ...withRaw,
    }
  }
  if (actual !== disproof.quote) {
    return {
      verdict: "stands",
      why: `the refuter's quote does not match ${disproof.path}:${disproof.line} byte for byte`,
      reasoning,
      ...withRaw,
    }
  }
  if (isSameLine(finding, disproof)) {
    // ⚠️ Quoting the finding's own line back is not a disproof, it is agreement about where to look.
    return {
      verdict: "stands",
      why: `the refuter re-quoted the line the finding already cites (${disproof.path}:${disproof.line}) rather than citing something that contradicts it`,
      reasoning,
      ...withRaw,
    }
  }

  return {
    verdict: "refuted",
    why: why === "" ? `disproved by ${disproof.path}:${disproof.line}` : why,
    reasoning,
    disproof,
    ...withRaw,
  }
}

function isSameLine(finding: CandidateFinding, evidence: RefutationEvidence): boolean {
  return finding.anchor.path === evidence.path && finding.anchor.line === evidence.line
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  if (typeof v === "string") {
    try {
      return asRecord(JSON.parse(v))
    } catch {
      return undefined
    }
  }
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined
}

function asString(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined
}

function readEvidence(v: unknown): RefutationEvidence | undefined {
  const r = asRecord(v)
  if (r === undefined) return undefined
  const path = asString(r["path"])
  const quote = asString(r["quote"])
  const line = r["line"]
  if (path === undefined || path === "" || quote === undefined) return undefined
  if (typeof line !== "number" || !Number.isSafeInteger(line) || line < 1) return undefined
  return { path, line, quote }
}
